import { api } from '@/lib/api'

export type Event = {
  id: string
  agenda_id: string
  name: string
  notes: string | null
  event_date: string
  event_time: string | null
}

export type AgendaEvent = {
  id: string
  agenda_id: string
  name: string
  status: string
  event_date: string | null
  event_time: string | null
}

export type EventAddress = {
  state: string
  city: string
  neighborhood: string
  street: string
  number: string
  complement: string | null
  latitude: number | null
  longitude: number | null
  full_address: string
  static_map_url: string | null
}

export type EventExpense = {
  id: number
  category: {
    id: number
    name: string
  }
  description: string
  amount: number
  created_at: string
}

export type EventFinancial = {
  cache_value: number
  total_expenses: number
  final_result: number
  expenses: EventExpense[]
}

export type Creator = {
  id: string
  name: string
  avatar_url: string | null
  email: string
}

export type EventReminder = {
  id: number
  event_id: string
  agenda_id: string
  reminder_date: string
  note: string | null
}

export type EventDetail = {
  id: string
  agenda_id: string
  name: string
  status: string
  event_date: string | null
  event_time: string | null
  notes: string | null
  contractor_name: string | null
  contractor_phone: string | null
  creator: Creator
  created_at: string
  address: EventAddress | null
  financial: EventFinancial | null
  reminders: EventReminder[]
}

export type CreateEventReminderRequest = {
  reminder_date: string
  note: string | null
}

export type CreateEventPayload = {
  name: string
  notes: string | null
  event_date: string
  event_time: string | null
  status: number
  contractor_name: string | null
  contractor_phone: string | null
  reminders: CreateEventReminderRequest[] | null
}

export type UpdateEventPayload = {
  name: string
  notes: string | null
  event_date: string
  event_time: string | null
  status: number
  contractor_name: string | null
  contractor_phone: string | null
}

export type UpdateAddressPayload = {
  state: string
  city: string
  neighborhood: string | null
  street: string | null
  number: string | null
  complement: string | null
}

export type UpdateFinancialPayload = {
  cache_value: number
}

export type CreateEventExpensePayload = {
  amount: number
  description: string
  category_id: number
}

export type UpdateEventExpensePayload = {
  amount: number
  description: string
  category_id: number
}

export const createEventService = (agendaId: string, payload: CreateEventPayload): Promise<Event> =>
  api.post<Event>(`/agendas/${agendaId}/events`, payload).then((res) => res.data)

export const updateEventService = (agendaId: string, eventId: string, payload: UpdateEventPayload): Promise<Event> =>
  api.put<Event>(`/agendas/${agendaId}/events/${eventId}`, payload).then((res) => res.data)

export const getAgendaEventsService = (agendaId: string): Promise<AgendaEvent[]> =>
  api.get<AgendaEvent[]>(`/agendas/${agendaId}/events`).then((res) => res.data)

export const getEventByIdService = (agendaId: string, eventId: string): Promise<EventDetail> =>
  api.get<EventDetail>(`/agendas/${agendaId}/events/${eventId}`).then((res) => res.data)

export const updateEventAddressService = (
  agendaId: string,
  eventId: string,
  payload: UpdateAddressPayload,
): Promise<void> =>
  api.put(`/agendas/${agendaId}/events/${eventId}/address`, payload).then((res) => res.data)

export const updateEventFinancialService = (
  agendaId: string,
  eventId: string,
  payload: UpdateFinancialPayload,
): Promise<void> =>
  api.put(`/agendas/${agendaId}/events/${eventId}/financial`, payload).then((res) => res.data)

export const createEventExpenseService = (
  agendaId: string,
  eventId: string,
  payload: CreateEventExpensePayload,
): Promise<EventExpense> =>
  api.post<EventExpense>(`/agendas/${agendaId}/events/${eventId}/expenses`, payload).then((res) => res.data)

export const updateEventExpenseService = (
  agendaId: string,
  eventId: string,
  expenseId: number,
  payload: UpdateEventExpensePayload,
): Promise<EventExpense> =>
  api
    .put<EventExpense>(`/agendas/${agendaId}/events/${eventId}/expenses/${expenseId}`, payload)
    .then((res) => res.data)

export const deleteEventExpenseService = (
  agendaId: string,
  eventId: string,
  expenseId: number,
): Promise<void> =>
  api.delete(`/agendas/${agendaId}/events/${eventId}/expenses/${expenseId}`).then((res) => res.data)

export const deleteEventService = (agendaId: string, eventId: string): Promise<void> =>
  api.delete(`/agendas/${agendaId}/events/${eventId}`).then((res) => res.data)

export type ReminderPayload = {
  reminder_date: string
  note: string | null
}

export const createEventReminderService = (
  agendaId: string,
  eventId: string,
  payload: ReminderPayload,
): Promise<EventReminder> =>
  api.post<EventReminder>(`/agendas/${agendaId}/events/${eventId}/reminders`, payload).then((res) => res.data)

export const updateEventReminderService = (
  agendaId: string,
  eventId: string,
  reminderId: number,
  payload: ReminderPayload,
): Promise<EventReminder> =>
  api.put<EventReminder>(`/agendas/${agendaId}/events/${eventId}/reminders/${reminderId}`, payload).then((res) => res.data)

export const deleteEventReminderService = (
  agendaId: string,
  eventId: string,
  reminderId: number,
): Promise<void> =>
  api.delete(`/agendas/${agendaId}/events/${eventId}/reminders/${reminderId}`).then((res) => res.data)
